import type { Candle, StructuralZone } from '../types/market.js';
import { averageTrueRange } from './indicators.js';
import { clamp, mean, percentChange } from './number.js';

interface Pivot {
  price: number;
  index: number;
  kind: 'support' | 'resistance' | 'impulse-origin';
  volume: number;
  source: string;
}

function completedCandles(candles: Candle[]): Candle[] {
  const latest = candles.at(-1);
  return latest && !latest.closed ? candles.slice(0, -1) : candles;
}

function findPivots(candles: Candle[], radius: number): Pivot[] {
  const pivots: Pivot[] = [];
  for (let index = radius; index < candles.length - radius; index += 1) {
    const candle = candles[index];
    if (!candle) continue;
    const neighborhood = candles.slice(index - radius, index + radius + 1);
    if (neighborhood.every((item) => candle.low <= item.low)) {
      pivots.push({ price: candle.low, index, kind: 'support', volume: candle.volume, source: 'swing low' });
    }
    if (neighborhood.every((item) => candle.high >= item.high)) {
      pivots.push({ price: candle.high, index, kind: 'resistance', volume: candle.volume, source: 'swing high' });
    }
  }
  return pivots;
}

function findImpulseOrigins(candles: Candle[], atr: number, volumeBaseline: number): Pivot[] {
  const pivots: Pivot[] = [];
  candles.forEach((candle, index) => {
    const body = Math.abs(candle.close - candle.open);
    if (body < atr * 1.35 || candle.volume < volumeBaseline * 1.15) return;
    const bullish = candle.close > candle.open;
    pivots.push({
      price: bullish ? Math.min(candle.open, candle.low + atr * 0.18) : Math.max(candle.open, candle.high - atr * 0.18),
      index,
      kind: 'impulse-origin',
      volume: candle.volume,
      source: bullish ? 'bullish impulse origin' : 'bearish impulse origin',
    });
  });
  return pivots;
}

function clusterPivots(pivots: Pivot[], tolerance: number): Pivot[][] {
  const sorted = [...pivots].sort((left, right) => left.price - right.price);
  const clusters: Pivot[][] = [];
  for (const pivot of sorted) {
    let best: Pivot[] | undefined;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const cluster of clusters) {
      const distance = Math.abs(mean(cluster.map((item) => item.price)) - pivot.price);
      if (distance <= tolerance && distance < bestDistance) {
        best = cluster;
        bestDistance = distance;
      }
    }
    if (best) best.push(pivot);
    else clusters.push([pivot]);
  }
  return clusters;
}

export function detectStructuralZones(candles: Candle[], currentPrice: number, maximum = 6): StructuralZone[] {
  const history = completedCandles(candles);
  if (history.length < 12 || currentPrice <= 0) return [];
  const atr = averageTrueRange(history, Math.min(14, history.length - 1)).filter(Number.isFinite).at(-1) ?? currentPrice * 0.005;
  const tolerance = Math.max(atr * 0.38, currentPrice * 0.0012);
  const radius = history.length >= 180 ? 3 : 2;
  const volumeBaseline = mean(history.slice(-Math.min(80, history.length)).map((candle) => candle.volume)) || 1;
  const pivots = [...findPivots(history, radius), ...findImpulseOrigins(history, atr, volumeBaseline)];

  return clusterPivots(pivots, tolerance)
    .map((cluster, index): StructuralZone => {
      const prices = cluster.map((pivot) => pivot.price);
      const center = mean(prices);
      const latestIndex = Math.max(...cluster.map((pivot) => pivot.index));
      const earliestIndex = Math.min(...cluster.map((pivot) => pivot.index));
      const ageCandles = history.length - 1 - latestIndex;
      const historyDepthCandles = history.length - earliestIndex;
      const averageVolume = mean(cluster.map((pivot) => pivot.volume));
      const uniqueSources = [...new Set(cluster.map((pivot) => pivot.source))];
      const touchScore = clamp(cluster.length * 12, 0, 42);
      const volumeScore = clamp((averageVolume / volumeBaseline) * 15, 0, 25);
      const recencyScore = clamp(25 - Math.log2(ageCandles + 1) * 5, 0, 25);
      const diversityScore = clamp(uniqueSources.length * 4, 0, 8);
      const strength = Math.round(clamp(touchScore + volumeScore + recencyScore + diversityScore, 0, 100));
      const kind = center <= currentPrice ? 'support' : 'resistance';
      const source = [
        `${cluster.length} historical reaction${cluster.length === 1 ? '' : 's'}`,
        ...uniqueSources.slice(0, 3),
        ageCandles < 12 ? 'recent structure' : `${ageCandles} candles old`,
        averageVolume > volumeBaseline * 1.2 ? 'high-volume formation' : 'normal-volume formation',
      ];
      const low = Math.min(...prices) - tolerance * 0.42;
      const high = Math.max(...prices) + tolerance * 0.42;
      const distancePercent = percentChange(center, currentPrice);
      const quality = strength >= 70 ? 'strong' : strength >= 45 ? 'moderate' : 'weak';
      const description = kind === 'support'
        ? `This is a ${quality} potential support zone. A controlled reaction with weakening sell pressure may preserve a bullish or recovery thesis. Sustained acceptance below it weakens that interpretation, while a fast sweep and reclaim still needs follow-through.`
        : `This is a ${quality} potential resistance zone. Rejection with fading spot demand can weaken continuation. A break is more credible when price holds beyond the zone with broad participation and survives a retest.`;

      return {
        id: `${kind}-${index}-${Math.round(center * 1_000_000)}`,
        kind,
        low,
        high,
        strength,
        touches: cluster.length,
        ageCandles,
        historyDepthCandles,
        distancePercent,
        source,
        description,
      };
    })
    .filter((zone) => Math.abs(zone.distancePercent) < 25)
    .sort((left, right) => {
      const leftDistance = Math.abs(left.distancePercent);
      const rightDistance = Math.abs(right.distancePercent);
      return leftDistance - rightDistance || right.strength - left.strength;
    })
    .slice(0, maximum);
}
